import React, { useRef } from 'react'
import { Box, Typography, IconButton, Grid, Stack, Tooltip } from '@mui/material'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'
import DeleteIcon from '@mui/icons-material/Delete'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import imageCompression from 'browser-image-compression'

const MAX_SIZE_KB = 200
const MAX_WIDTH = 1920

const compressionOptions = {
  maxSizeMB: MAX_SIZE_KB / 1024,
  maxWidthOrHeight: MAX_WIDTH,
  useWebWorker: true,
  fileType: 'image/webp',
  initialQuality: 0.75,
}

/**
 * PhotoUploader - sube hasta `maxPhotos` imágenes.
 * - Si la imagen ya pesa ≤ 200 KB se guarda sin modificar.
 * - Si pesa > 200 KB se comprime a WebP con calidad ajustada.
 * - La foto "principal" se marca con una estrella; se puede cambiar tocando otra foto.
 *
 * Props:
 *   photos            – array de { file?, preview, originalName, size, isNew?, isExisting?, id? }
 *   onChange          – (photos: array) => void
 *   primaryIndex      – índice de la foto principal (default 0)
 *   onPrimaryChange   – (index: number) => void
 *   maxPhotos         – límite de fotos (default 4)
 *   onDeleteExisting  – (photoId) => void  — callback al eliminar una foto ya guardada en backend
 */
const PhotoUploader = ({
  photos = [],
  onChange,
  primaryIndex = 0,
  onPrimaryChange,
  maxPhotos = 4,
  minPhotos = 0,
  onDeleteExisting,
}) => {
  const fileInputRef = useRef(null)

  const handleFileSelect = async e => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const availableSlots = maxPhotos - photos.length
    const filesToProcess = files.slice(0, availableSlots)

    const processed = []
    for (const file of filesToProcess) {
      try {
        let processedFile = file
        // Comprimir solo si supera el límite
        if (file.size > MAX_SIZE_KB * 1024) {
          processedFile = await imageCompression(file, compressionOptions)
        }
        const preview = URL.createObjectURL(processedFile)
        processed.push({
          file: processedFile,
          preview,
          originalName: file.name,
          size: processedFile.size,
          isNew: true,
        })
      } catch (err) {
        console.error('Error procesando imagen:', err)
      }
    }

    const newPhotos = [...photos, ...processed]
    onChange(newPhotos)
    // Si es la primera foto que se agrega, marcarla como principal automáticamente
    if (photos.length === 0 && processed.length > 0) {
      onPrimaryChange?.(0)
    }
    e.target.value = ''
  }

  const handleRemove = index => {
    const photo = photos[index]
    if (photo?.isExisting && photo?.id) {
      onDeleteExisting?.(photo.id)
    }
    const updated = photos.filter((_, i) => i !== index)
    onChange(updated)
    if (onPrimaryChange) {
      if (updated.length === 0) {
        onPrimaryChange(0)
      } else if (index === primaryIndex) {
        // Se eliminó la principal → primera restante
        onPrimaryChange(0)
      } else if (index < primaryIndex) {
        // Se eliminó una foto antes de la principal → ajustar índice
        onPrimaryChange(primaryIndex - 1)
      }
    }
  }

  const handleSetPrimary = index => {
    if (index === primaryIndex) return
    onPrimaryChange?.(index)
  }

  const emptySlots = maxPhotos - photos.length

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <CameraAltIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="body2" fontWeight={500}>
          Fotografías del árbol ({photos.length}/{maxPhotos})
        </Typography>
      </Stack>

      {minPhotos > 0 && (
        <Typography
          variant="caption"
          color={photos.length < minPhotos ? 'warning.main' : 'success.main'}
          sx={{ display: 'block', mb: 1 }}>
          {photos.length < minPhotos
            ? `Mínimo recomendado: ${minPhotos} fotos (faltan ${minPhotos - photos.length}).`
            : `Mínimo de ${minPhotos} fotos cumplido.`}
        </Typography>
      )}

      {photos.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Toca una foto para marcarla como principal
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Grid container spacing={1.5}>
        {photos.map((photo, index) => {
          const isPrimary = index === primaryIndex
          return (
            <Grid item xs={6} sm={3} key={index}>
              <Tooltip title={isPrimary ? 'Foto principal' : 'Marcar como principal'} arrow>
                <Box
                  onClick={() => handleSetPrimary(index)}
                  sx={{
                    position: 'relative',
                    paddingTop: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: isPrimary ? 'warning.main' : 'primary.light',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: isPrimary ? 'warning.dark' : 'primary.main' },
                  }}
                >
                  <Box
                    component="img"
                    src={photo.preview}
                    alt={`Foto ${index + 1}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  {/* Indicador de foto principal */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      bgcolor: isPrimary ? 'warning.main' : 'rgba(0,0,0,0.35)',
                      borderRadius: '50%',
                      width: 26,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {isPrimary ? (
                      <StarIcon sx={{ fontSize: 16, color: 'white' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16, color: 'white' }} />
                    )}
                  </Box>

                  {/* Botón eliminar */}
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation()
                      handleRemove(index)
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                      width: 28,
                      height: 28,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>

                  {/* Etiqueta de tamaño + badge "PRINCIPAL" */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: isPrimary ? 'warning.main' : 'rgba(0,0,0,0.6)',
                      color: 'white',
                      textAlign: 'center',
                      py: 0.25,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>
                      {isPrimary
                        ? 'PRINCIPAL'
                        : photo.isExisting
                          ? 'GUARDADA'
                          : `${(photo.size / 1024).toFixed(0)}KB`}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            </Grid>
          )
        })}

        {Array.from({ length: emptySlots }).map((_, index) => (
          <Grid item xs={6} sm={3} key={`empty-${index}`}>
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                paddingTop: '100%',
                position: 'relative',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}
              >
                <AddAPhotoIcon sx={{ fontSize: 28, color: 'text.secondary', mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary" display="block">
                  Agregar
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default PhotoUploader
